import { effect, inject, Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { UserStateControllerService } from '@generated/backend-api/api/user-state-controller.service';
import { PushSubscriptionDto } from '@generated/backend-api/model/push-subscription-dto';
import { UserStateService } from '@services/user-state/user-state.service';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  readonly swPush = inject(SwPush);
  readonly userStateControllerService = inject(UserStateControllerService);
  readonly userStateService = inject(UserStateService);

  constructor() {
    // Subscribe to incoming push messages
    this.swPush.messages.subscribe((message) => {
      console.debug('Received push message:', message);
    });

    // Follow the users' push notification settings to subscribe or unsubscribe from push notifications
    effect(() => {
      if (this.userStateService.state.pushNotificationsEnabled()) {
        this.subscribeToNotifications();
      } else {
        this.unsubscribeFromNotifications();
      }
    });
  }

  private subscribeToNotifications() {
    this.swPush
      .requestSubscription({
        serverPublicKey: environment.vapidPublicKey,
      })
      .then((sub) => {
        console.log('Successfully subscribed to notifications.');
        this.storeSubscription(sub);
      })
      .catch((err) => console.error('Could not subscribe to notifications', err));
  }

  private unsubscribeFromNotifications() {
    this.swPush
      .unsubscribe()
      .then(() => console.log('Successfully unsubscribed from notifications.'))
      .catch((err) => err); // prevent a console error when there was no subscription to unsubscribe from
  }

  storeSubscription(sub: PushSubscription) {
    let dto = {
      endpoint: sub.endpoint,
      keys: {
        auth: this.arrayBufferToBase64(sub.getKey('auth')),
        p256dh: this.arrayBufferToBase64(sub.getKey('p256dh')),
      },
      expirationTime: sub.expirationTime ? new Date(sub.expirationTime).toISOString() : null,
    } as PushSubscriptionDto;

    this.userStateControllerService.addPushSubscription(dto).subscribe();
  }

  private arrayBufferToBase64(buffer: ArrayBuffer | null): string | null {
    if (!buffer) return null;

    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}
